import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Eye, Globe, FileText } from 'lucide-react';

export default function AdminVisits() {
    const [data, setData] = useState(null);
    const [days, setDays] = useState('7');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/admin/visits?days=${days}`)
            .then(r => setData(r.data))
            .catch(e => console.error(e))
            .finally(() => setLoading(false));
    }, [days]);

    return (
        <div className="space-y-6" data-testid="admin-visits">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <h1 className="text-2xl font-heading font-bold text-cp-text-main">Visitas</h1>
                    <p className="text-cp-text-muted text-sm">Registro de visitas de la web</p>
                </div>
                <Select value={days} onValueChange={setDays}>
                    <SelectTrigger className="w-44" data-testid="visits-period">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="1">Hoy</SelectItem>
                        <SelectItem value="7">Ultimos 7 dias</SelectItem>
                        <SelectItem value="30">Ultimos 30 dias</SelectItem>
                        <SelectItem value="90">Ultimos 90 dias</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Total */}
            <div className="bg-white border border-cp-border rounded-xl p-5 flex items-center gap-4">
                <div className="w-12 h-12 bg-cp-primary-soft rounded-xl flex items-center justify-center">
                    <Eye size={22} className="text-cp-brand" />
                </div>
                <div>
                    <p className="text-cp-text-muted text-sm">Total de visitas</p>
                    <p className="font-heading font-bold text-2xl text-cp-text-main">{data?.total || 0}</p>
                </div>
            </div>

            {/* Chart */}
            {data?.daily?.length > 0 && (
                <div className="bg-white border border-cp-border rounded-xl p-5" data-testid="visits-chart">
                    <h2 className="font-heading font-bold text-cp-text-main mb-4">Visitas por dia</h2>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data.daily}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#EAF1F6" />
                                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#8A94A3' }} tickFormatter={v => v.slice(5)} />
                                <YAxis tick={{ fontSize: 11, fill: '#8A94A3' }} />
                                <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #D9E4EC', fontSize: '12px' }} />
                                <Bar dataKey="visits" fill="#6EC6F4" radius={[6, 6, 0, 0]} name="Visitas" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
                {/* Top Pages */}
                <div className="bg-white border border-cp-border rounded-xl p-5" data-testid="top-pages">
                    <div className="flex items-center gap-2 mb-4">
                        <FileText size={16} className="text-cp-primary" />
                        <h2 className="font-heading font-bold text-cp-text-main">Paginas mas visitadas</h2>
                    </div>
                    {data?.pages?.length > 0 ? (
                        <ul className="space-y-2">
                            {data.pages.map((p, i) => (
                                <li key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-cp-border-soft last:border-0">
                                    <span className="text-cp-text-body truncate mr-4">{p.page}</span>
                                    <span className="text-cp-text-main font-semibold shrink-0">{p.visits}</span>
                                </li>
                            ))}
                        </ul>
                    ) : <p className="text-cp-text-muted text-sm">Sin datos</p>}
                </div>

                {/* Top Referrers */}
                <div className="bg-white border border-cp-border rounded-xl p-5" data-testid="top-referrers">
                    <div className="flex items-center gap-2 mb-4">
                        <Globe size={16} className="text-cp-primary" />
                        <h2 className="font-heading font-bold text-cp-text-main">Fuentes de trafico</h2>
                    </div>
                    {data?.referrers?.length > 0 ? (
                        <ul className="space-y-2">
                            {data.referrers.map((r, i) => (
                                <li key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-cp-border-soft last:border-0">
                                    <span className="text-cp-text-body truncate mr-4">{r.referrer}</span>
                                    <span className="text-cp-text-main font-semibold shrink-0">{r.visits}</span>
                                </li>
                            ))}
                        </ul>
                    ) : <p className="text-cp-text-muted text-sm">Sin datos</p>}
                </div>
            </div>

            {/* Recent visits table */}
            {data?.recent?.length > 0 && (
                <div className="bg-white border border-cp-border rounded-xl overflow-hidden" data-testid="recent-visits">
                    <div className="p-5 border-b border-cp-border-soft">
                        <h2 className="font-heading font-bold text-cp-text-main">Visitas recientes</h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead><tr className="border-b border-cp-border-soft bg-cp-bg-alt">
                                <th className="text-left p-3 text-cp-text-muted font-medium">Pagina</th>
                                <th className="text-left p-3 text-cp-text-muted font-medium hidden md:table-cell">Origen</th>
                                <th className="text-left p-3 text-cp-text-muted font-medium hidden lg:table-cell">Sesion</th>
                                <th className="text-left p-3 text-cp-text-muted font-medium">Fecha</th>
                            </tr></thead>
                            <tbody>
                                {data.recent.slice(0, 20).map((v, i) => (
                                    <tr key={i} className="border-b border-cp-border-soft last:border-0">
                                        <td className="p-3 text-cp-text-main">{v.page}</td>
                                        <td className="p-3 text-cp-text-body text-xs hidden md:table-cell truncate max-w-[200px]">{v.referrer || 'Directo'}</td>
                                        <td className="p-3 text-cp-text-muted text-xs hidden lg:table-cell font-mono">{v.session_id?.slice(0,12)}</td>
                                        <td className="p-3 text-cp-text-muted text-xs">{v.created_at?.slice(0,19).replace('T', ' ')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
